import React, { useState } from 'react';

/**
 * DomainCardContainer - Standardized wrapper for all domain card UIs
 * Provides consistent styling and card info display with inline expandable description
 *
 * @param {string} cardKey - Card identifier
 * @param {object} card - Card data from allData.domainCards
 * @param {string} cardType - Override for card type display (e.g., "Spell", "Reaction")
 * @param {string} description - Override description (defaults to card summary)
 * @param {string} fullDescriptionOverride - Override for expanded description (supports **bold**)
 * @param {React.ReactNode} children - Action buttons and indicators
 */
const DomainCardContainer = ({
  cardKey,
  card,
  cardType,
  description,
  fullDescriptionOverride,
  children
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const displayType = cardType || card?.cardType || 'Ability';
  const displayDescription = description || `${card?.domain || 'Domain'} card`;

  // Full card description for expansion - use override if provided
  const fullDescription = fullDescriptionOverride || card?.text || card?.description || displayDescription;
  const hasFullDescription = fullDescription && fullDescription !== displayDescription;

  // Parse **bold** markdown syntax in text
  const parseMarkdown = (text) => {
    if (!text) return text;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="text-purple-300">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  // Toggle expansion - both triangle and text are clickable
  const toggleExpand = () => {
    if (hasFullDescription) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div
      key={cardKey}
      className="p-3 bg-gray-800 rounded-lg border border-gray-700"
    >
      <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
        {/* Card Info */}
        <div className="flex-grow min-w-0">
          <h4 className="font-bold text-purple-300 flex items-center">
            {card?.name || cardKey}
          </h4>
          <p className="text-xs text-purple-400">
            {card?.domain || 'Unknown'} | Lvl {card?.level || '?'} | {displayType}
          </p>
        </div>
        {/* Action Buttons (passed as children) */}
        {children}
      </div>
      {/* Description - entire row is clickable to expand */}
      <div
        onClick={toggleExpand}
        className={`flex items-start gap-1.5 ${hasFullDescription ? 'cursor-pointer hover:bg-gray-700/30 -mx-1 px-1 rounded transition-colors' : ''}`}
      >
        <div className="flex-grow">
          <p className={`text-gray-400 text-xs ${isExpanded ? 'whitespace-pre-wrap' : ''}`}>
            {isExpanded ? parseMarkdown(fullDescription) : parseMarkdown(displayDescription)}
          </p>
        </div>
        {/* Chevron on RIGHT - down when collapsed, up when expanded */}
        {hasFullDescription && (
          <span className="flex-shrink-0 text-purple-400">
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        )}
      </div>
    </div>
  );
};

export default DomainCardContainer;
